import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Upload, Sparkles, Wand2, Trash2, Download, Share2,
  Eye, Copy, ImageIcon, Video as VideoIcon, Check, Star, Pencil,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { adCreativesAPI } from '../lib/api';
import { chunkedUpload } from '../lib/chunkedUpload';

export default function AdCreativeSetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [set, setSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState({ name: '', percent: 0, index: 0, total: 0 });
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const load = useCallback(async (silent) => {
    try {
      if (!silent) setLoading(true);
      const res = await adCreativesAPI.getSet(id);
      setSet(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load set');
    } finally { if (!silent) setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Poll while bulk title generation runs
  const bulkStatus = set?.bulk_titles?.status;
  useEffect(() => {
    if (bulkStatus !== 'running') return undefined;
    const t = setInterval(() => load(true), 2500);
    return () => clearInterval(t);
  }, [bulkStatus, load]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadInfo({ name: f.name, percent: 0, index: i + 1, total: files.length });
        await chunkedUpload(f, 'ad_creative_media', id, (p) => setUploadInfo((u) => ({ ...u, percent: p })));
      }
      toast.success(`Uploaded ${files.length} file${files.length === 1 ? '' : 's'}`);
      await load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadInfo({ name: '', percent: 0, index: 0, total: 0 });
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleGenerateAll = async () => {
    try {
      await adCreativesAPI.generateAllTitles(id);
      toast.success('Generating titles for all media…');
      await load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start generation');
    }
  };

  const saveName = async () => {
    const n = nameDraft.trim();
    setEditingName(false);
    if (!n || n === set.name) return;
    try {
      const res = await adCreativesAPI.renameSet(id, n);
      setSet((s) => ({ ...s, name: res.data.name }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    }
  };

  const onMediaChange = (updated) => {
    setSet((s) => ({ ...s, media: (s.media || []).map((m) => (m.id === updated.id ? { ...m, ...updated } : m)) }));
  };
  const onMediaDelete = (mid) => setSet((s) => ({ ...s, media: (s.media || []).filter((m) => m.id !== mid) }));

  if (loading) return <div className="flex items-center justify-center py-24 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!set) return null;

  const media = set.media || [];
  const bulk = set.bulk_titles;

  return (
    <div className="max-w-6xl mx-auto" data-testid="ad-set-detail">
      <button onClick={() => navigate('/ad-creatives')} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4" data-testid="back-to-sets">
        <ArrowLeft className="w-4 h-4" /> All sets
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div className="min-w-0">
          {editingName ? (
            <Input
              autoFocus value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
              className="text-xl font-semibold h-10 max-w-md"
              data-testid="set-name-edit-input"
            />
          ) : (
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 inline-flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {set.name}
              <button onClick={() => { setNameDraft(set.name); setEditingName(true); }} className="text-zinc-300 hover:text-orange-600" data-testid="edit-set-name-btn"><Pencil className="w-4 h-4" /></button>
            </h1>
          )}
          <p className="text-sm text-zinc-500 mt-1 truncate">Listing: {set.listing_title}</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" multiple accept="image/*,video/*" onChange={handleFiles} className="hidden" data-testid="ad-media-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-zinc-300" data-testid="add-media-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Add media
          </Button>
          <Button onClick={handleGenerateAll} disabled={!media.length || bulk?.status === 'running'} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="generate-all-titles-btn">
            {bulk?.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
            {bulk?.status === 'running' ? `Generating ${bulk.done}/${bulk.total}` : 'Generate all titles'}
          </Button>
        </div>
      </div>

      {uploading && (
        <div className="mb-5 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-sm text-zinc-700 flex items-center gap-3" data-testid="ad-upload-progress">
          <Loader2 className="w-4 h-4 animate-spin text-orange-600" />
          <span className="truncate flex-1">Uploading {uploadInfo.index}/{uploadInfo.total} · {uploadInfo.name}</span>
          <span className="font-mono text-xs">{uploadInfo.percent}%</span>
        </div>
      )}

      {media.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 rounded-2xl py-20 text-center" data-testid="ad-media-empty">
          <Upload className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-600 font-medium">Add images & videos to this set</p>
          <p className="text-zinc-400 text-sm mt-1">The first image becomes the set thumbnail.</p>
          <Button onClick={() => fileRef.current?.click()} className="mt-5 bg-orange-600 hover:bg-orange-700 text-white" data-testid="add-media-empty-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Add media
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="ad-media-grid">
          {media.map((m) => (
            <MediaCard
              key={m.id} setId={id} media={m}
              isThumb={m.r2_key === set.thumbnail_r2_key}
              onChange={onMediaChange} onDelete={onMediaDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MediaCard({ setId, media, isThumb, onChange, onDelete }) {
  const [headline, setHeadline] = useState(media.headline || '');
  const [primary, setPrimary] = useState(media.primary_text || '');
  const [keywords, setKeywords] = useState((media.keywords || []).join(', '));
  const [genLoading, setGenLoading] = useState(false);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    setHeadline(media.headline || '');
    setPrimary(media.primary_text || '');
    setKeywords((media.keywords || []).join(', '));
  }, [media.headline, media.primary_text, media.keywords]);

  const copy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 1200);
    toast.success('Copied');
  };

  const saveField = async (patch) => {
    try {
      const res = await adCreativesAPI.editMedia(setId, media.id, patch);
      onChange({ id: media.id, ...res });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleGenerate = async () => {
    try {
      setGenLoading(true);
      const res = await adCreativesAPI.generateTitle(setId, media.id);
      onChange({ id: media.id, ...res });
      toast.success('Title generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenLoading(false); }
  };

  const handleShare = () => {
    if (media.share_url) { navigator.clipboard.writeText(media.share_url); toast.success('Share link copied'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this media from the set?')) return;
    try {
      await adCreativesAPI.deleteMedia(setId, media.id);
      onDelete(media.id);
      toast.success('Removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const status = media.title_status;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid={`ad-media-card-${media.id}`}>
      <div className="relative bg-zinc-900 aspect-video flex items-center justify-center overflow-hidden">
        {media.kind === 'video' ? (
          <video src={media.view_url} controls className="w-full h-full object-contain" data-testid={`ad-media-video-${media.id}`} />
        ) : (
          <img src={media.view_url} alt={headline || 'creative'} className="w-full h-full object-contain" data-testid={`ad-media-image-${media.id}`} />
        )}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-medium bg-black/60 text-white rounded-full px-2 py-0.5">
          {media.kind === 'video' ? <VideoIcon className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />} {media.kind}
        </span>
        {isThumb && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-medium bg-orange-600 text-white rounded-full px-2 py-0.5" data-testid={`thumb-badge-${media.id}`}>
            <Star className="w-3 h-3" /> Thumbnail
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* AI actions row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={handleGenerate} disabled={genLoading || status === 'pending'} className="h-8 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid={`gen-title-btn-${media.id}`}>
            {(genLoading || status === 'pending') ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            AI title
          </Button>
          <a href={media.view_url} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" className="h-8" data-testid={`view-btn-${media.id}`}><Eye className="w-3.5 h-3.5 mr-1.5" />View</Button>
          </a>
          <a href={media.download_url}>
            <Button size="sm" variant="outline" className="h-8" data-testid={`download-btn-${media.id}`}><Download className="w-3.5 h-3.5 mr-1.5" />Download</Button>
          </a>
          <Button size="sm" variant="outline" onClick={handleShare} className="h-8" data-testid={`share-btn-${media.id}`}><Share2 className="w-3.5 h-3.5 mr-1.5" />Share</Button>
          <button onClick={handleDelete} className="ml-auto text-zinc-400 hover:text-red-600 p-1.5" data-testid={`delete-media-${media.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>

        {status === 'failed' && <p className="text-[11px] text-red-600">Generation failed: {media.title_error || 'unknown error'}</p>}
        {status === 'pending' && <p className="text-[11px] text-orange-600 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Writing ad copy…</p>}

        {/* Headline */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Headline <span className="text-zinc-300">({headline.length}/40)</span></label>
            <button onClick={() => copy(headline, 'h')} className="text-zinc-400 hover:text-orange-600" data-testid={`copy-headline-${media.id}`}>{copied === 'h' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}</button>
          </div>
          <Input
            value={headline} onChange={(e) => setHeadline(e.target.value)}
            onBlur={() => headline !== (media.headline || '') && saveField({ headline })}
            placeholder="Meta ad headline…" className="h-8 text-sm" data-testid={`headline-input-${media.id}`}
          />
        </div>

        {/* Primary text */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Primary text</label>
            <button onClick={() => copy(primary, 'p')} className="text-zinc-400 hover:text-orange-600" data-testid={`copy-primary-${media.id}`}>{copied === 'p' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}</button>
          </div>
          <textarea
            value={primary} onChange={(e) => setPrimary(e.target.value)}
            onBlur={() => primary !== (media.primary_text || '') && saveField({ primary_text: primary })}
            placeholder="One-line hook for the ad…" rows={2}
            className="w-full text-sm border border-zinc-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-400 resize-none"
            data-testid={`primary-input-${media.id}`}
          />
        </div>

        {/* Keywords */}
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1 block">Keywords</label>
          <Input
            value={keywords} onChange={(e) => setKeywords(e.target.value)}
            onBlur={() => saveField({ keywords })}
            placeholder="comma, separated, keywords" className="h-8 text-xs" data-testid={`keywords-input-${media.id}`}
          />
        </div>
      </div>
    </div>
  );
}
